import Phaser from "phaser";
import { ENEMIES } from "../config";
import { Enemy } from "../entities/Enemy";
import { sound } from "./Sound";
import {
  generateEscalatingWave,
  generateEscalatingWaves,
  getThemeDef,
  SCRIPTED_WAVE_TARGET,
  type Wave,
} from "./waveGeneration";

export type { Wave } from "./waveGeneration";

/** Delay (in scaled game-ms) between auto-continued waves. */
const AUTO_CONTINUE_DELAY_MS = 3000;

/**
 * First waves are hand-tuned. After these, the shared generator produces
   * progressively harder mixes through wave 60 and then supports Endless mode.
 *
 * To add a new hand-tuned wave: append to HAND_WAVES.
 * To extend the scripted late game further: bump SCRIPTED_WAVE_TARGET.
 */
const HAND_WAVES: Wave[] = [
  { reward: 50,  entries: [{ type: "goblin", count: 8, interval: 700 }] },
  { reward: 60,  entries: [{ type: "goblin", count: 12, interval: 550 }] },
  { reward: 75,  entries: [{ type: "goblin", count: 8, interval: 500 }, { type: "skeleton", count: 4, interval: 700 }] },
  { reward: 90,  entries: [{ type: "skeleton", count: 14, interval: 450 }] },
  { reward: 110, entries: [{ type: "orc", count: 8, interval: 800 }] },
  { reward: 120, entries: [{ type: "swarmling", count: 24, interval: 180 }] },
  { reward: 130, entries: [{ type: "goblin", count: 20, interval: 300 }, { type: "orc", count: 5, interval: 1000 }] },
  { reward: 150, entries: [{ type: "wraith", count: 6, interval: 600 }, { type: "skeleton", count: 10, interval: 400 }] },
  { reward: 170, entries: [{ type: "flame_imp", count: 8, interval: 500 }, { type: "goblin", count: 12, interval: 350 }] },
  { reward: 200, entries: [{ type: "armored_orc", count: 5, interval: 900 }, { type: "orc", count: 8, interval: 600 }] },
  { reward: 220, entries: [{ type: "troll", count: 2, interval: 2000 }, { type: "orc", count: 10, interval: 600 }] },
  { reward: 280, entries: [{ type: "troll", count: 4, interval: 1500 }, { type: "skeleton", count: 25, interval: 250 }] },
  { reward: 450, entries: [{ type: "troll", count: 6, interval: 1400 }, { type: "armored_orc", count: 6, interval: 700 }, { type: "wraith", count: 8, interval: 400 }, { type: "swarmling", count: 30, interval: 150 }] }
];

const WAVES: Wave[] = [
  ...HAND_WAVES,
  ...generateEscalatingWaves(HAND_WAVES.length + 1, SCRIPTED_WAVE_TARGET - HAND_WAVES.length),
];

export type WaveEvent =
  | { kind: "wave-start"; index: number; total: number; boss: boolean }
  | { kind: "wave-cleared"; index: number; reward: number }
  | { kind: "auto-countdown"; remainingMs: number }
  | { kind: "all-cleared" };

export class WaveManager {
  private waveIndex = -1;
  private spawnQueue: { type: keyof typeof ENEMIES; atMs: number }[] = [];
  private elapsedMs = 0;
  private inWave = false;
  private listeners: ((e: WaveEvent) => void)[] = [];
  private scriptedWaveCount = 0;

  /**
   * When true, the next wave starts automatically after `AUTO_CONTINUE_DELAY_MS`
   * of scaled game-time elapses following a `wave-cleared`. Toggled from UI.
   */
  private autoContinue = false;
  private autoCooldownMs = 0;
  private spawnedCount = 0;

  constructor(
    private scene: Phaser.Scene,
    private waypoints: { x: number; y: number }[],
    private addEnemy: (e: Enemy) => void,
    private getActiveEnemies: () => Enemy[],
    /** Optional override wave script (used when running a campaign map). */
    customWaves?: Wave[],
    /** Optional secondary lane (e.g. river maps). 50/50 alternating spawn. */
    private altWaypoints?: { x: number; y: number }[],
    /** Difficulty multipliers applied to every spawned enemy. */
    private hpMult: number = 1,
    private rewardMult: number = 1,
    private waveTheme: import("./waveGeneration").WaveTheme = "plains",
    private difficulty: number = 1,
    private endlessMode: boolean = true
  ) {
    // Copy, never alias: getWave() caches generated Endless waves into this
    // array, and the caller's array outlives the run — CAMPAIGN_MAPS entries are
    // module-level and reused every time a map is replayed.
    this.waves = [...(customWaves ?? WAVES)];
    this.scriptedWaveCount = this.waves.length;
  }

  private waves: Wave[];

  on(fn: (e: WaveEvent) => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter(l => l !== fn);
    };
  }
  private emit(e: WaveEvent): void { for (const l of this.listeners) l(e); }

  get currentWaveNumber(): number { return this.waveIndex + 1; }
  get totalWaves(): number { return this.scriptedWaveCount; }
  get isInWave(): boolean { return this.inWave; }
  get hasMoreWaves(): boolean { return this.endlessMode || this.waveIndex + 1 < this.waves.length; }
  get isAutoContinue(): boolean { return this.autoContinue; }
  get autoCountdownMs(): number { return this.autoCooldownMs; }
  get isEndlessMode(): boolean { return this.endlessMode; }

  private getWave(index: number): Wave | null {
    const existing = this.waves[index];
    if (existing) return existing;
    if (!this.endlessMode) return null;

    const generated = generateEscalatingWave(index + 1, this.waveTheme);
    this.waves[index] = generated;
    return generated;
  }

  setAutoContinue(on: boolean): void {
    this.autoContinue = on;
    if (!on) {
      this.autoCooldownMs = 0;
    } else if (!this.inWave && this.hasMoreWaves && this.autoCooldownMs <= 0) {
      // Toggling ON between waves immediately schedules the next one.
      this.autoCooldownMs = AUTO_CONTINUE_DELAY_MS;
    }
  }

  startNextWave(): boolean {
    if (this.inWave || !this.hasMoreWaves) return false;
    this.waveIndex++;
    const wave = this.getWave(this.waveIndex);
    if (!wave) {
      this.waveIndex--;
      return false;
    }
    this.spawnQueue = [];
    this.elapsedMs = 0;
    this.autoCooldownMs = 0;
    let t = 0;
    const entries = [...wave.entries];
    const waveNumber = this.waveIndex + 1;
    // Boss rounds get the escalation too, otherwise the finale is the one wave
    // Knight and Lord leave untouched — it ends up softer than the wave before
    // it precisely where the campaign should peak.
    if (waveNumber >= 10 && this.difficulty >= 2) {
      const themed = getThemeDef(this.waveTheme);
      entries.push({ type: themed.skirmisher, count: 2 + Math.floor(waveNumber / 10), interval: 620 });
      if (this.difficulty >= 3) entries.push({ type: themed.elite, count: 1 + Math.floor(waveNumber / 15), interval: 860 });
    }
    for (const entry of entries) {
      for (let i = 0; i < entry.count; i++) {
        this.spawnQueue.push({ type: entry.type, atMs: t });
        const pressureMult = this.difficulty >= 3 ? 0.9 : this.difficulty >= 2 ? 0.96 : 1;
        t += entry.interval * pressureMult;
      }
    }
    this.inWave = true;
    this.emit({ kind: "wave-start", index: this.waveIndex, total: this.totalWaves, boss: !!wave.boss });
    sound.playWave(!!wave.boss);
    return true;
  }

  update(_time: number, deltaMs: number): void {
    // Auto-continue countdown ticks even while inWave===false.
    if (!this.inWave && this.autoContinue && this.hasMoreWaves && this.autoCooldownMs > 0) {
      this.autoCooldownMs -= deltaMs;
      this.emit({ kind: "auto-countdown", remainingMs: Math.max(0, this.autoCooldownMs) });
      if (this.autoCooldownMs <= 0) {
        this.autoCooldownMs = 0;
        this.startNextWave();
      }
    }

    if (!this.inWave) return;
    this.elapsedMs += deltaMs;

    while (this.spawnQueue.length > 0 && this.spawnQueue[0].atMs <= this.elapsedMs) {
      const next = this.spawnQueue.shift()!;
      const baseStats = ENEMIES[next.type];
      if (!baseStats) continue; // unknown type — skip silently to keep map data forward-compatible
      const waveNumber = this.waveIndex + 1;
      const rampWaves = Math.max(0, waveNumber - 8);
      // Base wave scaling to keep later waves challenging even on lower difficulties
      const baseWaveScale = 1 + Math.max(0, waveNumber - 10) * 0.028;
      // Both ramps grow with the wave index, so raising them adds pressure to
      // the late game a fed-up skill tree trivialises without touching the
      // opening waves the player has to survive on two towers.
      const difficultyRamp = this.difficulty >= 3
        ? 1 + rampWaves * 0.024
        : this.difficulty >= 2 ? 1 + rampWaves * 0.016 : 1;
      const effectiveHpMult = this.hpMult * difficultyRamp * baseWaveScale;
      const stats = (effectiveHpMult === 1 && this.rewardMult === 1) ? baseStats : {
        ...baseStats,
        hp: Math.round(baseStats.hp * effectiveHpMult),
        reward: Math.round(baseStats.reward * this.rewardMult)
      };
      // Pick a lane: alternate when a secondary path exists.
      let path = this.waypoints;
      if (this.altWaypoints) {
        path = (this.spawnedCount++ % 2 === 0) ? this.waypoints : this.altWaypoints;
      }
      const enemy = new Enemy(this.scene, stats, path);
      this.addEnemy(enemy);
    }

    if (this.spawnQueue.length === 0 && this.getActiveEnemies().length === 0) {
      const reward = this.waves[this.waveIndex].reward;
      this.inWave = false;
      this.emit({ kind: "wave-cleared", index: this.waveIndex, reward });
      if (!this.hasMoreWaves) {
        this.emit({ kind: "all-cleared" });
      } else if (this.autoContinue) {
        this.autoCooldownMs = AUTO_CONTINUE_DELAY_MS;
      }
    }
  }
}

